import React, { useCallback } from 'react';
import { getCustomFundMetadata, getHoldingsCategories, type Fund, type HoldingsBenchmarkCategory } from 'venn-api';
import { createFixedStrategiesKey, useHasFF, useQuery } from 'venn-utils';
import type { StrategyAssignments } from '../../modals/data-uploader/hooks/useAssignedPortfolios';
import { buildStrategyPathNames } from '../../modals/data-uploader/utils/strategyUtils';

export type ManualAssignment = {
  fundId: string;
  categoryId: number;
  hierarchyLevels: string[];
};

export const useAssignedAllocatorPortfolios = () => {
  const hasManualStrategyAssignment = useHasFF('rcm_portfolio_strategy_enforcement_ff');

  // Store the fundId for refetching
  const fundIdRef = React.useRef<string | undefined>();

  // Fetch full taxonomy
  const { data: categories = [] } = useQuery<HoldingsBenchmarkCategory[]>(
    createFixedStrategiesKey('ASSET', 'WORKSPACE'),
    async () => (await getHoldingsCategories('ASSET', 'WORKSPACE')).content || [],
    { staleTime: 10 * 60 * 1000, keepPreviousData: true, enabled: hasManualStrategyAssignment },
  );

  // Fetch server assignments per fund id
  const assignmentsQuery = useQuery<StrategyAssignments>(
    ['assignments'],
    async () => {
      if (!hasManualStrategyAssignment || !fundIdRef.current) return {} as StrategyAssignments;
      const { content } = await getCustomFundMetadata([fundIdRef.current]);
      return content || {};
    },
    {
      enabled: false, // Only run when refetched
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
    },
  );

  const findInvestmentAssignment = async (fundId?: string) => {
    fundIdRef.current = fundId;

    if (!hasManualStrategyAssignment || !fundId) {
      return undefined;
    }

    const assignmentsQueryResult = await assignmentsQuery.refetch();

    return assignmentsQueryResult.data && Object.values(assignmentsQueryResult.data).length
      ? assignmentsQueryResult.data
      : undefined;
  };

  const assignInvestment = useCallback(
    (fund: Fund, categoryId: string) => {
      if (!hasManualStrategyAssignment) return null;

      const parsedCategoryId = parseInt(categoryId, 10);
      if (Number.isFinite(parsedCategoryId)) {
        const hierarchyLevels = buildStrategyPathNames(categories, parsedCategoryId);
        if (!hierarchyLevels?.length) return null;

        return {
          fundId: fund.id,
          categoryId: parsedCategoryId,
          hierarchyLevels,
        };
      }
      return null;
    },
    [categories, hasManualStrategyAssignment],
  );

  return {
    assignInvestment,
    findInvestmentAssignment,
    hasManualStrategyAssignment,
    categories,
  };
};
